import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Redirect, Route, Switch, useLocation, useParams } from "wouter";
import {
  ArrowDown,
  ArrowRight,
  Download,
  FileArchive,
  GripVertical,
  ImagePlus,
  LayoutDashboard,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Sparkles,
  Trash2,
  Upload,
  Users,
  BookOpen,
} from "lucide-react";
import { api } from "./api";
import {
  CharacterCard,
  ConfirmDialog,
  DocumentButton,
  EditButton,
  Layout,
  Loader,
  MediaCarousel,
  PageIntro,
} from "./components";
import { useApp } from "./context";
import type { Character, Location, Media } from "./types";
import ConversationReader from "./ConversationReader";

function Home() {
  const { content, loading } = useApp();
  if (loading || !content) return <Loader />;
  const { settings, characters, locations } = content;
  return (
    <>
      <section className={settings.hero_image || settings.hero_mobile_image ? "hero has-image" : "hero"}>
        {(settings.hero_image || settings.hero_mobile_image) && (
          <picture className="hero-picture">
            <source media="(max-width: 760px)" srcSet={settings.hero_mobile_image || settings.hero_image} />
            <img src={settings.hero_image || settings.hero_mobile_image} alt="" />
          </picture>
        )}
        {!settings.hero_image && !settings.hero_mobile_image && (
          <div className="hero-cast" aria-hidden="true">
            {characters.slice(0, 5).map((character) => (
              <img key={character.id} src={character.card_image} alt="" />
            ))}
          </div>
        )}
        <div className="hero-content">
          <p className="hero-kicker">Family Game</p>
          <h1>{settings.hero_title}</h1>
          <p>{settings.hero_subtitle}</p>
          <Link className="button white" href="/postavy">
            Vstoupit do příběhu <ArrowRight size={18} />
          </Link>
        </div>
        <a className="scroll-cue" href="#postavy">
          Objevovat <ArrowDown size={16} />
        </a>
      </section>

      <section className="content-section" id="postavy">
        <div className="section-heading wide">
          <div>
            <p className="eyebrow">01 / Postavy</p>
            <h2>Každý má svůj příběh.</h2>
          </div>
          <Link className="text-link" href="/postavy">Všechny postavy <ArrowRight size={18} /></Link>
        </div>
        <div className="characters-grid">
          {characters.map((character, index) => (
            <CharacterCard key={character.id} character={character} index={index} />
          ))}
        </div>
      </section>

      <section
        className={settings.city_teaser_image ? "city-teaser has-image" : "city-teaser"}
        style={settings.city_teaser_image ? { backgroundImage: `linear-gradient(90deg, rgba(4,4,4,.9), rgba(4,4,4,.28)), url(${settings.city_teaser_image})` } : undefined}
      >
        <div>
          <p className="eyebrow light-text">02 / Město</p>
          <h2>Příběh má své<br />ulice a adresy</h2>
          <p>{locations.length} míst čeká na objevení.</p>
          <Link className="button white" href="/mesto">Prozkoumat město <ArrowRight size={18} /></Link>
        </div>
        <div className="city-lines" aria-hidden="true" />
      </section>
    </>
  );
}

function CharactersPage() {
  const { content, user, loading } = useApp();
  const [search, setSearch] = useState("");
  if (loading || !content) return <Loader />;
  const editor = user?.role === "admin" || user?.role === "collaborator";
  const filtered = content.characters.filter((character) =>
    `${character.first_name} ${character.last_name} ${character.job_title}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="page-shell">
      <PageIntro
        eyebrow="Obsazení"
        title="Postavy"
        text={`${content.characters.length} životů. Každý s vlastní minulostí, vztahy a rozhodnutími.`}
        action={editor && <Link className="button dark" href="/postavy/nova"><Plus size={18} /> Přidat postavu</Link>}
      />
      <label className="character-search">
        <Search size={19} />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Hledat postavu podle jména nebo pracovní pozice…"
        />
      </label>
      <div className="characters-grid">
        {filtered.map((character, index) => (
          <CharacterCard key={character.id} character={character} index={index} />
        ))}
      </div>
      {!filtered.length && <div className="empty-state compact">Žádná postava neodpovídá hledanému výrazu.</div>}
    </div>
  );
}

function CharacterPage() {
  const { slug } = useParams();
  const { user } = useApp();
  const [, navigate] = useLocation();
  const [character, setCharacter] = useState<Character | null>(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api<Character>(`/characters/${slug}`).then(setCharacter).catch((cause) => setError(cause.message));
  }, [slug]);

  if (error) return <NotFound text={error} />;
  if (!character) return <Loader />;
  const media = (category: string) => character.media?.filter((item) => item.category === category) || [];
  const editor = user?.role === "admin" || user?.role === "collaborator";

  async function remove() {
    await api(`/records/character/${character!.id}`, { method: "DELETE" });
    navigate("/postavy");
  }

  return (
    <article className="character-detail">
      <div className="detail-back-wrap">
        <Link href="/postavy" className="detail-back"><ArrowRight size={16} /> Zpět na přehled postav</Link>
      </div>
      <section className="character-profile">
        <div className="character-biography">
          <p className="eyebrow">Character file</p>
          <h1>
            <strong>{character.first_name.toUpperCase()}</strong>
            <span>{character.last_name}</span>
          </h1>
          <p className="role-label">{character.job_title}</p>
          <div className="story-card">
            <h3><Sparkles size={19} /> Popis a příběhové pozadí postavy</h3>
            <div className="long-copy">
              {character.description.split("\n").map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
          </div>
        </div>
        <div className="character-visuals">
          <div className="main-portrait">
            <img src={character.main_image || character.card_image} alt={`${character.first_name} ${character.last_name}`} />
          </div>
          <div className="gif-grid">
            {media("gif").slice(0, 6).map((item) => (
              <img key={item.id} src={item.url} alt={item.alt || character.first_name} />
            ))}
            {!media("gif").length && <div className="gif-placeholder">GIF galerie čeká na první moment.</div>}
          </div>
          {media("gif").length > 6 && <button className="button ghost">Další gifíčky</button>}
          {editor && (
            <div className="edit-tools">
              <EditButton to={`/postavy/${character.slug}/upravit`} label="Upravit postavu" />
              {user?.role === "admin" && (
                <button className="icon-button danger-outline" onClick={() => setConfirm(true)} aria-label="Smazat postavu">
                  <Trash2 />
                </button>
              )}
            </div>
          )}
        </div>
      </section>
      <div className="profile-archive">
        <MediaCarousel title="Garáž" items={media("garage")} empty="Garáž je zatím prázdná." variant="garage" />
        <MediaCarousel title="House" items={media("house")} empty="Fotky domu budou doplněny." />
        <MediaCarousel title="Mazlíčci" items={media("pets")} empty="Žádní mazlíčci zatím nebyli přidáni." />
      </div>
      {confirm && (
        <ConfirmDialog
          title="Smazat postavu?"
          text={`Postava ${character.first_name} ${character.last_name} a všechna její média budou definitivně odstraněny.`}
          onCancel={() => setConfirm(false)}
          onConfirm={remove}
        />
      )}
    </article>
  );
}

type CharacterDraft = Pick<
  Character,
  "first_name" | "last_name" | "job_title" | "description" | "card_image" | "main_image" | "sort_order" | "published"
> & { slug: string };

const emptyCharacter: CharacterDraft = {
  first_name: "",
  last_name: "",
  job_title: "",
  description: "",
  card_image: "",
  main_image: "",
  sort_order: 0,
  published: 1,
  slug: "",
};

function CharacterEditor() {
  const { slug } = useParams();
  const { user, refresh } = useApp();
  const [, navigate] = useLocation();
  const isNew = slug === "nova";
  const [character, setCharacter] = useState<(Character & { media: Media[] }) | CharacterDraft>(emptyCharacter);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<Record<string, File[]>>({});

  useEffect(() => {
    if (!isNew) {
      api<Character & { media: Media[] }>(`/characters/${slug}`).then(setCharacter).catch((cause) => setStatus(cause.message));
    }
  }, [isNew, slug]);

  if (!user || user.role === "reader") return <Redirect to="/prihlaseni" replace />;
  const update = (key: keyof CharacterDraft, value: string | number) =>
    setCharacter((current) => ({ ...current, [key]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      const result = await api<{ id: string; slug: string }>(
        isNew ? "/characters" : `/characters/${(character as Character).id}`,
        { method: isNew ? "POST" : "PUT", body: JSON.stringify(character) },
      );
      if (isNew) {
        for (const [category, files] of Object.entries(pending)) {
          for (const file of files) await sendFile(result.id, category, file);
        }
      }
      await refresh();
      if (isNew) navigate(`/postavy/${result.slug}/upravit`, { replace: true });
      else setStatus("Změny jsou uložené.");
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  }

  async function sendFile(owner: string, category: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    await api(`/upload?scope=character&owner=${owner}&category=${category}`, { method: "POST", body: form });
  }

  async function uploadFile(category: string, file?: File) {
    if (!file) return;
    if (!("id" in character)) {
      setPending((current) => ({
        ...current,
        [category]: category === "card" || category === "main" ? [file] : [...(current[category] || []), file],
      }));
      setStatus("Soubor je připravený a nahraje se spolu s profilem.");
      return;
    }
    setStatus("Nahrávám soubor…");
    try {
      await sendFile(character.id, category, file);
      const fresh = await api<Character & { media: Media[] }>(`/characters/${character.slug}`);
      setCharacter(fresh);
      await refresh();
      setStatus("Soubor je nahraný.");
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : "Nahrání selhalo.");
    }
  }

  async function deleteMedia(item: Media) {
    await api(`/media-records/character/${item.id}`, { method: "DELETE" });
    setCharacter((current) => ({
      ...current,
      media: "media" in current ? current.media.filter((media) => media.id !== item.id) : [],
    }));
  }

  const media = "media" in character ? character.media : [];
  return (
    <div className="page-shell narrow">
      <PageIntro
        eyebrow="Editor postavy"
        title={isNew ? "Nová postava" : `${character.first_name} ${character.last_name}`}
        text={isNew ? "Vyplňte profil a rovnou vyberte všechny fotografie i gifíčky. Nahrají se při uložení." : "Změny se po uložení projeví ihned."}
      />
      <form className="editor-card" onSubmit={submit}>
        <div className="form-grid two">
          <label>Jméno<input value={character.first_name} onChange={(e) => update("first_name", e.target.value)} required /></label>
          <label>Příjmení<input value={character.last_name} onChange={(e) => update("last_name", e.target.value)} /></label>
          <label>Pracovní pozice<input value={character.job_title} onChange={(e) => update("job_title", e.target.value)} placeholder="mayor" /></label>
          <label>Adresa profilu<input value={character.slug} onChange={(e) => update("slug", e.target.value)} placeholder="vytvoří se automaticky" /></label>
        </div>
        <label>Popis<textarea rows={12} value={character.description} onChange={(e) => update("description", e.target.value)} /></label>
        <div className="form-grid two">
          <label>Pořadí<input type="number" value={character.sort_order} onChange={(e) => update("sort_order", Number(e.target.value))} /></label>
          <label className="checkbox"><input type="checkbox" checked={Boolean(character.published)} onChange={(e) => update("published", e.target.checked ? 1 : 0)} /> Veřejně zobrazit</label>
        </div>
        <div className="form-actions">
          <button className="button dark" disabled={saving}><Save size={17} /> {saving ? "Ukládám…" : "Uložit profil"}</button>
          {!isNew && <Link className="button ghost" href={`/postavy/${character.slug}`}>Zobrazit profil</Link>}
          {status && <span className="form-status">{status}</span>}
        </div>
      </form>

      <section className="upload-panel">
          <div className="section-heading"><div><p className="eyebrow">Média</p><h2>Obrázky a gify</h2></div></div>
          <div className="upload-grid">
            <UploadBox title="Foto na kartě" accept="image/*" onFile={(file) => uploadFile("card", file)} preview={character.card_image} queuedCount={pending.card?.length} />
            <UploadBox title="Hlavní foto 3:4" accept="image/*" onFile={(file) => uploadFile("main", file)} preview={character.main_image} queuedCount={pending.main?.length} />
            {(["gif", "garage", "house", "pets"] as const).map((category) => (
              <UploadBox
                key={category}
                title={{ gif: "Gifíčky", garage: "Garáž", house: "House", pets: "Mazlíčci" }[category]}
                accept={category === "gif" ? "image/gif,image/webp" : "image/*"}
                multiple
                onFile={(file) => uploadFile(category, file)}
                queuedCount={pending[category]?.length}
              />
            ))}
          </div>
          {"id" in character && <div className="media-manager">
            {media.map((item) => (
              <div key={item.id}>
                <img src={item.url} alt="" />
                <span>{item.category}</span>
                <button type="button" onClick={() => deleteMedia(item)} aria-label="Smazat médium"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>}
        </section>
    </div>
  );
}

function UploadBox({
  title,
  accept,
  preview,
  multiple,
  onFile,
  queuedCount,
}: {
  title: string;
  accept: string;
  preview?: string;
  multiple?: boolean;
  onFile: (file?: File) => void;
  queuedCount?: number;
}) {
  return (
    <label className="upload-box">
      {preview ? <img src={preview} alt="" /> : <ImagePlus />}
      <span><strong>{title}</strong><small>{queuedCount ? `Vybráno: ${queuedCount}` : "Klikněte nebo přetáhněte soubor"}</small></span>
      <input type="file" accept={accept} multiple={multiple} onChange={(e) => {
        Array.from(e.target.files || []).forEach(onFile);
        e.target.value = "";
      }} />
    </label>
  );
}

function SettingUpload({
  title,
  preview,
  accept = "image/*",
  onFile,
  onRemove,
}: {
  title: string;
  preview: string;
  accept?: string;
  onFile: (file?: File) => void;
  onRemove: () => void;
}) {
  return (
    <div className="setting-upload">
      <UploadBox title={title} accept={accept} preview={preview} onFile={onFile} />
      {preview && (
        <button type="button" className="remove-upload" onClick={onRemove}>
          <Trash2 size={15} /> Odstranit
        </button>
      )}
    </div>
  );
}

function CityPage() {
  const { content, user, loading } = useApp();
  if (loading || !content) return <Loader />;
  return (
    <div className="page-shell">
      <PageIntro
        eyebrow="Mapa příběhu"
        title="Město"
        text="Lokace, které spojují osudy postav. Každá ulice může skrývat další kapitolu."
        action={user?.role === "admin" && <Link className="button dark" href="/mesto/nova/upravit"><Plus size={18} /> Přidat lokaci</Link>}
      />
      {content.settings.city_page_banner && (
        <div className="city-page-strip">
          <img src={content.settings.city_page_banner} alt="" />
        </div>
      )}
      <div className="locations-list">
        {content.locations.map((location) => (
          <Link
            key={location.id}
            href={`/mesto/${location.slug}`}
            className={location.banner_image ? "location-banner has-image" : "location-banner"}
            style={location.banner_image ? { backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.92), rgba(0,0,0,.2)), url(${location.banner_image})` } : undefined}
          >
            <div className="location-banner-copy">
              <p className="location-kicker"><MapPin size={15} /> Lokace &amp; městský okrsek</p>
              <h2>{location.name}</h2>
              <p className="location-subtitle">{location.subtitle || "Místo s vlastním příběhem"}</p>
              <span className="location-cta">Prozkoumat lokaci <ArrowRight size={17} /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function LocationPage() {
  const { slug } = useParams();
  const { user } = useApp();
  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<Location>(`/locations/${slug}`).then(setLocation).catch((cause) => setError(cause.message));
  }, [slug]);
  if (error) return <NotFound text={error} />;
  if (!location) return <Loader />;
  return (
    <article>
      <section
        className={location.banner_image ? "location-hero has-image" : "location-hero"}
        style={location.banner_image ? { backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.84), rgba(0,0,0,.18)), url(${location.banner_image})` } : undefined}
      >
        <p className="eyebrow light-text">{location.subtitle || "Lokace"}</p>
        <h1>{location.name}</h1>
      </section>
      <div className="location-body">
        <div>
          <p className="eyebrow">O místě</p>
          <div className="long-copy">{location.description.split("\n").map((p, i) => <p key={i}>{p}</p>)}</div>
          {user?.role === "admin" && <EditButton to={`/mesto/${location.slug}/upravit`} label="Upravit lokaci" />}
        </div>
        <div className="location-gallery">
          {location.media?.map((item) => <img key={item.id} src={item.url} alt={item.alt || location.name} />)}
          {!location.media?.length && <div className="empty-media">Galerie této lokace čeká na fotografie.</div>}
        </div>
      </div>
    </article>
  );
}

const emptyLocation = { name: "", slug: "", subtitle: "", description: "", banner_image: "", sort_order: 0, published: 1 };

function LocationEditor() {
  const { slug } = useParams();
  const { user, refresh } = useApp();
  const [, navigate] = useLocation();
  const isNew = slug === "nova";
  const [location, setLocation] = useState<Location | typeof emptyLocation>(emptyLocation);
  const [status, setStatus] = useState("");
  const [pending, setPending] = useState<Record<"banner" | "gallery", File[]>>({ banner: [], gallery: [] });
  useEffect(() => {
    if (!isNew) api<Location>(`/locations/${slug}`).then(setLocation).catch((cause) => setStatus(cause.message));
  }, [isNew, slug]);
  if (user?.role !== "admin") return <Redirect to="/prihlaseni" replace />;
  const update = (key: string, value: string | number) => setLocation((current) => ({ ...current, [key]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const result = await api<{ id: string; slug: string }>(
      isNew ? "/locations" : `/locations/${(location as Location).id}`,
      { method: isNew ? "POST" : "PUT", body: JSON.stringify(location) },
    );
    if (isNew) {
      for (const [category, files] of Object.entries(pending) as ["banner" | "gallery", File[]][]) {
        for (const file of files) await sendLocationFile(result.id, category, file);
      }
    }
    await refresh();
    if (isNew) navigate(`/mesto/${result.slug}/upravit`, { replace: true });
    else setStatus("Změny jsou uložené.");
  }
  async function sendLocationFile(owner: string, category: "banner" | "gallery", file: File) {
    const form = new FormData();
    form.append("file", file);
    await api(`/upload?scope=location&owner=${owner}&category=${category}`, { method: "POST", body: form });
  }
  async function uploadFile(category: "banner" | "gallery", file?: File) {
    if (!file) return;
    if (!("id" in location)) {
      setPending((current) => ({
        ...current,
        [category]: category === "banner" ? [file] : [...current.gallery, file],
      }));
      setStatus("Obrázek je připravený a nahraje se spolu s lokací.");
      return;
    }
    await sendLocationFile(location.id, category, file);
    const fresh = await api<Location>(`/locations/${location.slug}`);
    setLocation(fresh);
    await refresh();
    setStatus("Soubor je nahraný.");
  }
  return (
    <div className="page-shell narrow">
      <PageIntro eyebrow="Editor lokace" title={isNew ? "Nová lokace" : location.name} text="Doporučený banner: 1200 × 500 px." />
      <form className="editor-card" onSubmit={submit}>
        <div className="form-grid two">
          <label>Název<input value={location.name} onChange={(e) => update("name", e.target.value)} required /></label>
          <label>Podtitulek<input value={location.subtitle} onChange={(e) => update("subtitle", e.target.value)} /></label>
          <label>Adresa profilu<input value={location.slug} onChange={(e) => update("slug", e.target.value)} /></label>
          <label>Pořadí<input type="number" value={location.sort_order} onChange={(e) => update("sort_order", Number(e.target.value))} /></label>
        </div>
        <label>Popis<textarea rows={10} value={location.description} onChange={(e) => update("description", e.target.value)} /></label>
        <label className="checkbox"><input type="checkbox" checked={Boolean(location.published)} onChange={(e) => update("published", e.target.checked ? 1 : 0)} /> Veřejně zobrazit</label>
        <div className="form-actions"><button className="button dark"><Save size={17} /> Uložit lokaci</button>{status && <span>{status}</span>}</div>
      </form>
      <section className="upload-panel">
          <div className="section-heading"><div><p className="eyebrow">Obrázky lokace</p><h2>Banner a galerie</h2></div></div>
          <div className="upload-grid">
            <UploadBox title="Banner 1200 × 500" accept="image/*" preview={location.banner_image} queuedCount={pending.banner.length} onFile={(f) => uploadFile("banner", f)} />
            <UploadBox title="Galerie" accept="image/*" multiple queuedCount={pending.gallery.length} onFile={(f) => uploadFile("gallery", f)} />
          </div>
        </section>
    </div>
  );
}

function PlotPage() {
  const { content, user, loading, refresh } = useApp();
  const [status, setStatus] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState("");
  if (loading || !content) return <Loader />;

  async function uploadDocuments(files: FileList | null) {
    if (!files) return;
    setStatus("Nahrávám…");
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.replace(/\.[^.]+$/, ""));
      await api("/upload?scope=document", { method: "POST", body: form });
    }
    await refresh();
    setStatus("Soubory jsou nahrané.");
  }

  async function deleteDoc(id: string) {
    if (!window.confirm("Opravdu chcete tento soubor odstranit?")) return;
    await api(`/records/document/${id}`, { method: "DELETE" });
    await refresh();
  }

  async function replaceDoc(id: string, file: File | undefined) {
    if (!file) return;
    if (!window.confirm(`Nahradit obsah souboru za „${file.name}“? Uložená místa a zajímavé momenty zůstanou zachované.`)) return;
    setReplacingId(id);
    setStatus("Nahrazuji soubor…");
    try {
      const form = new FormData();
      form.append("file", file);
      await api(`/documents/${id}/replace`, { method: "POST", body: form });
      await refresh();
      setStatus("Soubor byl nahrazený. Záložky a rozečtená místa zůstala zachovaná.");
    } finally {
      setReplacingId(null);
    }
  }

  async function renameDoc(event: FormEvent, id: string) {
    event.preventDefault();
    setStatus("Přejmenovávám soubor…");
    await api(`/documents/${id}`, {
      method: "PUT",
      body: JSON.stringify({ title: documentTitle }),
    });
    await refresh();
    setRenamingId(null);
    setDocumentTitle("");
    setStatus("Soubor byl přejmenovaný.");
  }

  return (
    <div className="page-shell plot-page">
      <PageIntro
        eyebrow="Archiv"
        title="Plot&Prog"
        text="Historie konverzací a dokumenty příběhu seřazené tak, jak šel čas."
        action={content.documents.length > 0 && (
          <a className="button dark" href="/api/documents/download-all"><FileArchive size={18} /> Stáhnout vše .ZIP</a>
        )}
      />
      {user?.role === "admin" && (
        <label className="plot-upload">
          <Upload />
          <span><strong>Nahrát soubory historie</strong><small>Můžete vybrat více souborů najednou, maximálně 25 MB každý.</small></span>
          <input type="file" multiple onChange={(e) => uploadDocuments(e.target.files)} />
          {status && <em>{status}</em>}
        </label>
      )}
      <div className="documents-list">
        {content.documents.map((document) => (
          <div className="document-row" key={document.id}>
            <Link className="document-reader-link" href={`/plot-prog/${document.id}`}>
              <BookOpen />
              <span><strong>{document.title}</strong><small>Číst přímo v aplikaci</small></span>
              <ArrowRight />
            </Link>
            <DocumentButton url={document.url} title="Stáhnout originál" fileName={document.file_name} />
            {user?.role === "admin" && (
              <div className="document-actions">
                <label
                  className={`document-replace-button${replacingId === document.id ? " loading" : ""}`}
                  aria-label="Nahradit soubor novější verzí"
                  title="Nahradit soubor novější verzí a zachovat záložky"
                >
                  <RefreshCw />
                  <input
                    type="file"
                    disabled={replacingId !== null}
                    onChange={(event) => {
                      void replaceDoc(document.id, event.target.files?.[0]);
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
                <button
                  className="icon-button"
                  onClick={() => {
                    setRenamingId(document.id);
                    setDocumentTitle(document.title);
                  }}
                  aria-label="Přejmenovat soubor"
                  title="Přejmenovat soubor"
                >
                  <Pencil />
                </button>
                <button className="icon-button danger-outline" onClick={() => deleteDoc(document.id)} aria-label="Smazat soubor"><Trash2 /></button>
              </div>
            )}
            {renamingId === document.id && (
              <form className="document-rename-form" onSubmit={(event) => renameDoc(event, document.id)}>
                <label>
                  Nový název
                  <input value={documentTitle} onChange={(event) => setDocumentTitle(event.target.value)} autoFocus required />
                </label>
                <button className="button dark compact" type="submit"><Save size={16} /> Uložit název</button>
                <button className="button ghost compact" type="button" onClick={() => setRenamingId(null)}>Zrušit</button>
              </form>
            )}
          </div>
        ))}
        {!content.documents.length && (
          <div className="empty-state"><Download /><h2>Archiv je zatím prázdný</h2><p>Administrátor sem může nahrát první soubory historie.</p></div>
        )}
      </div>
    </div>
  );
}

function LoginPage() {
  const { user, refreshUser } = useApp();
  const [, navigate] = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  if (user) return <Redirect to={user.role === "reader" ? "/" : "/admin"} replace />;
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
      await refreshUser();
      navigate("/admin");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Přihlášení selhalo.");
    }
  }
  return (
    <div className="login-page">
      <section className="login-copy">
        <p className="eyebrow light-text">Private access</p>
        <h1>Vítejte<br />zpátky.</h1>
        <p>Přihlaste se pro práci s obsahem příběhového světa.</p>
      </section>
      <form className="login-form" onSubmit={submit}>
        <div><p className="eyebrow">Přihlášení</p><h2>Vstup do aplikace</h2></div>
        <label>Uživatelské jméno<input autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required /></label>
        <label>Heslo<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {error && <p className="form-error">{error}</p>}
        <button className="button dark" type="submit">Přihlásit <ArrowRight size={18} /></button>
        <small>Účty vytváří administrátor při prvním nasazení.</small>
      </form>
    </div>
  );
}

function AdminPage() {
  const { user, content, refresh } = useApp();
  const [settings, setSettings] = useState(content?.settings);
  const [status, setStatus] = useState("");
  const [orderedCharacters, setOrderedCharacters] = useState<Character[]>(content?.characters || []);
  const [draggedCharacter, setDraggedCharacter] = useState<string | null>(null);
  const [, navigate] = useLocation();
  useEffect(() => setSettings(content?.settings), [content]);
  useEffect(() => setOrderedCharacters(content?.characters || []), [content?.characters]);
  if (!user || user.role === "reader") return <Redirect to="/prihlaseni" replace />;
  if (!content || !settings) return <Loader />;

  async function saveSettings(event: FormEvent) {
    event.preventDefault();
    await api("/settings", { method: "PUT", body: JSON.stringify(settings) });
    await refresh();
    setStatus("Nastavení je uložené.");
  }
  type SettingImage = "hero_image" | "hero_mobile_image" | "logo_image" | "city_teaser_image" | "city_page_banner";
  async function uploadSetting(category: SettingImage, file?: File) {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    await api(`/upload?scope=setting&category=${category}`, { method: "POST", body: form });
    await refresh();
    setStatus("Grafika je nahraná.");
  }
  async function removeSetting(category: SettingImage) {
    await api(`/setting-media/${category}`, { method: "DELETE" });
    await refresh();
    setStatus("Grafika byla odstraněná.");
  }
  async function moveCharacter(targetId: string) {
    if (user?.role !== "admin" || !draggedCharacter || draggedCharacter === targetId) return;
    const next = [...orderedCharacters];
    const from = next.findIndex((character) => character.id === draggedCharacter);
    const to = next.findIndex((character) => character.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrderedCharacters(next);
    setDraggedCharacter(null);
    setStatus("Ukládám nové pořadí postav…");
    try {
      await api("/characters/reorder", {
        method: "PUT",
        body: JSON.stringify({ ids: next.map((character) => character.id) }),
      });
      await refresh();
      setStatus("Pořadí postav je uložené.");
    } catch (cause) {
      setOrderedCharacters(content?.characters || []);
      setStatus(cause instanceof Error ? cause.message : "Pořadí se nepodařilo uložit.");
    }
  }
  const cards = [
    { icon: Users, value: content.characters.length, label: "postav", to: "/postavy" },
    { icon: MapPin, value: content.locations.length, label: "lokací", to: "/mesto" },
    { icon: FileArchive, value: content.documents.length, label: "souborů", to: "/plot-prog" },
  ];
  return (
    <div className="page-shell admin-page">
      <PageIntro eyebrow="Content studio" title={`Ahoj, ${user.display_name}.`} text={user.role === "admin" ? "Odtud řídíte celý příběhový svět." : "Můžete spravovat a doplňovat profily postav."} />
      <div className="stats-grid">
        {cards.map(({ icon: Icon, value, label, to }) => (
          <Link href={to} key={label}><Icon /><strong>{value}</strong><span>{label}</span><ArrowRight /></Link>
        ))}
      </div>
      <section className="admin-section">
        <div className="section-heading">
          <div><p className="eyebrow">Rychlá správa</p><h2>Postavy</h2></div>
          <Link className="button dark" href="/postavy/nova"><Plus size={17} /> Přidat</Link>
        </div>
        <div className="admin-list">
          {orderedCharacters.map((character) => (
            <div
              key={character.id}
              className={`admin-character-row${draggedCharacter === character.id ? " dragging" : ""}`}
              draggable={user.role === "admin"}
              onDragStart={() => setDraggedCharacter(character.id)}
              onDragEnd={() => setDraggedCharacter(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => moveCharacter(character.id)}
            >
              {user.role === "admin" && <GripVertical className="drag-handle" aria-label="Přetáhnout postavu" />}
              <button onClick={() => navigate(`/postavy/${character.slug}/upravit`)}>
                <img src={character.card_image} alt="" />
                <span><strong>{character.first_name} {character.last_name}</strong><small>{character.job_title}</small></span>
                <Pencil />
              </button>
            </div>
          ))}
        </div>
        {status && <p className="admin-status">{status}</p>}
      </section>
      {user.role === "admin" && (
        <>
          <section className="admin-section">
            <div className="section-heading"><div><p className="eyebrow">Vzhled</p><h2>Landing page</h2></div></div>
            <form className="editor-card" onSubmit={saveSettings}>
              <div className="form-grid two">
                <label>Název / textové logo<input value={settings.site_title} onChange={(e) => setSettings({ ...settings, site_title: e.target.value })} /></label>
                <label>Hlavní nadpis<textarea rows={2} value={settings.hero_title} onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })} /></label>
              </div>
              <label>Úvodní text<textarea rows={3} value={settings.hero_subtitle} onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })} /></label>
              <div className="upload-grid">
                <SettingUpload title="Hlavní banner 1920 × 600" preview={settings.hero_image} onFile={(f) => uploadSetting("hero_image", f)} onRemove={() => removeSetting("hero_image")} />
                <SettingUpload title="Mobilní banner 900 × 1200" preview={settings.hero_mobile_image} onFile={(f) => uploadSetting("hero_mobile_image", f)} onRemove={() => removeSetting("hero_mobile_image")} />
                <SettingUpload title="Bílé logo — PNG/SVG" accept="image/*,.svg" preview={settings.logo_image} onFile={(f) => uploadSetting("logo_image", f)} onRemove={() => removeSetting("logo_image")} />
                <SettingUpload title="Banner sekce 02 / Město" preview={settings.city_teaser_image} onFile={(f) => uploadSetting("city_teaser_image", f)} onRemove={() => removeSetting("city_teaser_image")} />
                <SettingUpload title="Úzký banner stránky Město 1600 × 360" preview={settings.city_page_banner} onFile={(f) => uploadSetting("city_page_banner", f)} onRemove={() => removeSetting("city_page_banner")} />
              </div>
              <div className="form-actions"><button className="button dark"><Save size={17} /> Uložit texty</button>{status && <span>{status}</span>}</div>
            </form>
          </section>
          <section className="admin-section">
            <div className="section-heading"><div><p className="eyebrow">Obsah</p><h2>Město a Plot&Prog</h2></div></div>
            <div className="admin-shortcuts">
              <Link href="/mesto"><MapPin /> Spravovat lokace <ArrowRight /></Link>
              <Link href="/plot-prog"><FileArchive /> Spravovat soubory <ArrowRight /></Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function NotFound({ text = "Tahle stránka v příběhu není." }: { text?: string }) {
  return (
    <div className="not-found"><span>404</span><h1>{text}</h1><Link className="button dark" href="/">Zpět na začátek</Link></div>
  );
}

function App() {
  return (
    <Layout>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/postavy" component={CharactersPage} />
        <Route path="/postavy/nova" component={CharacterEditor} />
        <Route path="/postavy/:slug/upravit" component={CharacterEditor} />
        <Route path="/postavy/:slug" component={CharacterPage} />
        <Route path="/mesto" component={CityPage} />
        <Route path="/mesto/:slug/upravit" component={LocationEditor} />
        <Route path="/mesto/:slug" component={LocationPage} />
        <Route path="/plot-prog/:id" component={ConversationReader} />
        <Route path="/plot-prog" component={PlotPage} />
        <Route path="/prihlaseni" component={LoginPage} />
        <Route path="/admin" component={AdminPage} />
        <Route><NotFound /></Route>
      </Switch>
    </Layout>
  );
}

export default App;
